/**
 * computer-use-mcp — the single renderer for the `open-computer-use`
 * `.mcp.json` entry (ENG-10133, epic ENG-10127).
 *
 * WHY A SHARED RENDERER RATHER THAN TWO BLOCKS. The claudecode adapter writes
 * `.mcp.json` from two places: `buildMcpJson` (full provision) and the
 * incremental `writeMcpServer` sweep (a poll that picks up a newly-granted
 * integration without a reprovision). Every stdio integration before this one —
 * origami, augmented-help-kb, augmented-admin — is spelled out separately in
 * both, and the two copies are kept in step by hand. That is the shape ENG-8231
 * exists to police one directory over: a field present in one copy and absent
 * from the other produces no error, just an agent that is missing something.
 * This entry is rendered once and spliced into both.
 *
 * WHY THE UPSTREAM COMMAND IS ARGV. `computer-use-proxy` takes its upstream
 * after `--`. The credential does NOT travel here — only the key's eventual
 * PATH does, and that path is chosen by the proxy at runtime, not by this
 * function (ENG-10092: a secret in argv outlives the call in every process
 * listing on the host). See computer-use-ssh-identity.ts.
 *
 * WHY WE SSH TO LOOPBACK. The Mac opens a REVERSE tunnel (`ssh -R`) to the
 * agent host, so the agent host connects to its own `127.0.0.1:<port>` and the
 * Mac's own ssh client makes the final hop to `localhost:22`. That is what lets
 * the Mac's `authorized_keys` carry `from="127.0.0.1,::1"`: sshd on the Mac
 * sees the connection as originating locally. The pin is a load-bearing part of
 * the design, not an obstacle — a copy of the private key stolen from the agent
 * host is useless unless the Mac has itself opened the tunnel.
 */

import { join } from 'node:path';

import { remoteMcpEnvPrefix } from '../integrations/remote-mcp-connection.js';

/** Catalog `definition_id` / `code_name`. */
export const COMPUTER_USE_DEFINITION_ID = 'open-computer-use';

/**
 * The `.mcp.json` key, and therefore the tool prefix the agent sees
 * (`mcp__open-computer-use__*`) and the wildcard `--allowedTools` derives.
 * Deliberately equal to the definition id so the three never drift.
 */
export const COMPUTER_USE_MCP_KEY = 'open-computer-use';

/**
 * Env var the manager materializes the private key into.
 *
 * DERIVED, NOT CHOSEN, and the name is a compromise worth explaining. The
 * `.env.integrations` writer in the claudecode adapter publishes an `api_key`
 * row's credential under `<PREFIX>_API_KEY` — a convention, applied to every
 * integration, with no per-definition override short of declaring a `cli_tool`
 * this integration does not have. So a var named for what the value IS
 * (`..._SSH_KEY`) would have had no producer: the template in `.mcp.json` would
 * never substitute, the proxy would log "still the literal placeholder" on every
 * spawn, and the integration would be dead behind a perfectly healthy-looking
 * config file.
 *
 * Hence: the CONSTANT is named for the value (a reader here learns it is an SSH
 * key) and the VALUE follows the convention (so it is actually written). It is
 * computed from `remoteMcpEnvPrefix`, the same helper the writer uses, so the
 * two cannot drift even if the definition id changes.
 *
 * What is in it is a base64 OpenSSH PRIVATE KEY, not an API key.
 */
export const COMPUTER_USE_SSH_KEY_ENV = `${remoteMcpEnvPrefix(
  COMPUTER_USE_DEFINITION_ID,
  null,
)}_API_KEY`;

/** Loopback endpoint of the Mac's reverse tunnel, unless config overrides it. */
const DEFAULT_SSH_HOST = '127.0.0.1';
/** Default reverse-tunnel port. Overridable per install; two Macs on one host need two. */
const DEFAULT_SSH_PORT = 2222;
/** What to run on the Mac. Overridable because the binary's path varies by install. */
const DEFAULT_REMOTE_COMMAND = 'open-computer-use mcp';

/**
 * Characters permitted in `ssh_user`.
 *
 * MUST START ALPHANUMERIC, and that is the part doing real work: `user` and
 * `host` are joined into ONE argv element, so a leading `-` produces
 * `-foo@127.0.0.1`, which ssh reads as OPTIONS rather than a destination. No
 * `:` either — it means nothing in a username, and permitting it would make the
 * error message below untrue.
 */
const SAFE_SSH_USER = /^[A-Za-z0-9][A-Za-z0-9._-]*$/;

/**
 * The ONLY destinations `ssh_host` may name.
 *
 * An allowlist, not a character rule, and the difference is the whole point.
 * A character rule accepts `attacker.example` — which is well-formed, passes
 * every syntactic check, and points the connection at somebody else's machine.
 * That would hand our private key to that host and run the "remote command"
 * there, so the field would have been a redirect primitive rather than a
 * convenience.
 *
 * Loopback is not a restriction being worked around: it is what the design
 * says. The Mac opens a REVERSE tunnel (`ssh -R`), so the agent host connects
 * to its OWN loopback and the Mac's ssh client makes the final hop. A
 * non-loopback value cannot describe that topology — there is no legitimate
 * install it would serve. The port is the per-install knob; the host is not.
 *
 * (CodeRabbit, PR #5779. My previous rule was syntactic and admitted every
 * hostname on the internet.)
 */
const ALLOWED_SSH_HOSTS: readonly string[] = Object.freeze([
  '127.0.0.1',
  '::1',
  'localhost',
]);

/**
 * Shell metacharacters refused in `remote_command`. ssh gives the remote command
 * to a shell, so each of these can start a SECOND command on the Mac.
 */
const SHELL_METACHARACTERS = /[;|&`$()<>\n\r*?\[\]{}!#~'"\\]/;

export interface ComputerUseMcpInput {
  /** Agent UUID — baked in, since the gate posts it to the control plane. */
  agentId: string;
  /** Agent code_name — used only to place the per-agent known_hosts file. */
  agentCodeName: string;
  /** `agent_integrations.config` for the row. */
  config: Record<string, unknown> | null | undefined;
  /** Absolute path to `~/.augmented/_mcp/computer-use-proxy.js` on this host. */
  proxyPath: string;
  /** Absolute path to the agent's directory, for the known_hosts file. */
  agentDir: string;
}

export interface ComputerUseMcpEntry {
  command: string;
  args: string[];
  env: Record<string, string>;
}

export interface ComputerUseMcpResult {
  /** The rendered entry, or `null` when the install is not usable. */
  entry: ComputerUseMcpEntry | null;
  /**
   * Why no entry was rendered, for the operator. `null` on success.
   *
   * Returned rather than thrown because the caller is building the WHOLE
   * `.mcp.json`: one misconfigured integration must not cost the agent every
   * other server it has.
   */
  problem: string | null;
}

function readString(config: Record<string, unknown> | null | undefined, key: string): string | null {
  const v = config?.[key];
  return typeof v === 'string' && v.trim().length > 0 ? v.trim() : null;
}

function readPort(config: Record<string, unknown> | null | undefined): number | null {
  const v = config?.['ssh_port'];
  if (v === undefined || v === null || v === '') return DEFAULT_SSH_PORT;
  const n = typeof v === 'number' ? v : Number(v);
  // A non-integer or out-of-range port is a configuration error, not a value to
  // coerce: `Number('') === 0` and `Number('2222x') === NaN`, and either
  // silently rendered would produce an entry that fails at connect time with a
  // message about the port rather than about the config.
  if (!Number.isInteger(n) || n < 1 || n > 65535) return null;
  return n;
}

/**
 * Render the entry for one `open-computer-use` install.
 *
 * THIS FUNCTION IS THE LAST VALIDATION OF `config`, not the first. Until
 * ENG-10317 it was the only one: the seed's `config_schema` declared `ssh_user`
 * required and nothing read it, so an install with no username completed cleanly
 * and this function then refused it into a host log. The schema is now enforced
 * (`validateIntegrationConfig` refuses such an install or config PATCH, and the
 * console builds its form from it), and its `pattern` / `enum` mirror the checks
 * below. `eng-10317-integration-config-schema.test.ts` pins that mirror: every
 * config the schema accepts, this renders.
 *
 * The checks stay here regardless. A row written before the schema was enforced,
 * or written by hand, reaches this function without passing through either door.
 *
 * Returns `{ entry: null, problem }` when the install cannot produce a working
 * server — today that is a missing `ssh_user` or an unusable `ssh_port`. The
 * caller writes nothing in that case, which is correct: an entry that cannot
 * connect gives the agent tools it will be refused by, and gives the operator a
 * failure at first use rather than at grant time.
 */
export function buildComputerUseMcpEntry(input: ComputerUseMcpInput): ComputerUseMcpResult {
  const user = readString(input.config, 'ssh_user');
  if (user === null) {
    return {
      entry: null,
      problem:
        `${COMPUTER_USE_DEFINITION_ID}: config.ssh_user is required (the macOS account the ` +
        `tunnel authenticates as) — no MCP entry was written`,
    };
  }

  const port = readPort(input.config);
  if (port === null) {
    return {
      entry: null,
      problem: `${COMPUTER_USE_DEFINITION_ID}: config.ssh_port must be an integer 1-65535 — no MCP entry was written`,
    };
  }

  // `ssh_user` and `ssh_host` are joined into a single `user@host` argv element,
  // so a value carrying a space or an `@` silently changes which account on
  // which machine is reached.
  if (!SAFE_SSH_USER.test(user)) {
    return {
      entry: null,
      problem:
        `${COMPUTER_USE_DEFINITION_ID}: config.ssh_user must start with a letter or digit and ` +
        `contain only letters, digits, '.', '_' and '-' — no MCP entry was written`,
    };
  }

  const host = readString(input.config, 'ssh_host') ?? DEFAULT_SSH_HOST;
  if (!ALLOWED_SSH_HOSTS.includes(host)) {
    return {
      entry: null,
      problem:
        `${COMPUTER_USE_DEFINITION_ID}: config.ssh_host must be one of ` +
        `${ALLOWED_SSH_HOSTS.join(', ')} — the Mac opens a REVERSE tunnel, so the agent host ` +
        `connects to its own loopback. A different host would send this agent's key somewhere ` +
        `else. No MCP entry was written`,
    };
  }

  const remoteCommand = readString(input.config, 'remote_command') ?? DEFAULT_REMOTE_COMMAND;
  // ssh hands the remote command to a SHELL on the Mac, so a metacharacter here
  // is a second command running on someone's desktop. There is no agent-facing
  // writer of `agent_integrations`, but the human-facing one is wider than an
  // earlier version of this comment claimed ("only an authenticated admin"):
  // `PATCH /integrations/:id` checks team MEMBERSHIP, not role (ENG-10322), and ENG-10317 put
  // a console form in front of it. So this is load-bearing, not belt-and-braces —
  // it is what keeps a config write from being a remote shell, whoever makes it.
  //
  // Nothing legitimate needs these. The field names a binary and its arguments
  // (`/usr/local/bin/node /opt/ocu mcp`); chaining, substitution and redirection
  // are not part of that job.
  if (SHELL_METACHARACTERS.test(remoteCommand)) {
    return {
      entry: null,
      problem:
        `${COMPUTER_USE_DEFINITION_ID}: config.remote_command must name a binary and its arguments ` +
        `and may not contain shell metacharacters (; | & \` $ ( ) < > newline) — no MCP entry was written`,
    };
  }

  // Per-agent known_hosts. NOT /dev/null with StrictHostKeyChecking=no: the
  // endpoint is loopback, but what answers on it is the Mac's sshd reached
  // through the tunnel, so its host key is a real thing worth pinning.
  // `accept-new` pins on first connection and refuses a CHANGE thereafter,
  // which is the property that matters — a different key on a port that is
  // supposed to be one person's Mac is exactly the event to stop on.
  const knownHosts = join(input.agentDir, '.computer-use-known-hosts');

  return {
    problem: null,
    entry: {
      command: 'node',
      args: [
        input.proxyPath,
        '--',
        'ssh',
        // -T: no pty. The upstream speaks JSON-RPC over stdio; a pty would
        // inject terminal control sequences into the stream.
        '-T',
        // Never prompt. Without this a missing or rejected key makes ssh sit
        // waiting for a passphrase on a stdin that is carrying JSON-RPC, and
        // the server appears to hang rather than to fail.
        '-o', 'BatchMode=yes',
        '-o', 'StrictHostKeyChecking=accept-new',
        '-o', `UserKnownHostsFile=${knownHosts}`,
        '-o', 'ConnectTimeout=10',
        // Keepalives so a dropped tunnel surfaces as a closed stdio pipe
        // rather than a silently wedged child. ENG-10134 owns detecting that
        // and respawning; this just makes the event happen.
        '-o', 'ServerAliveInterval=15',
        '-o', 'ServerAliveCountMax=3',
        '-p', String(port),
        `${user}@${host}`,
        remoteCommand,
      ],
      env: {
        // The three the approval gate reads (ENG-10132). Deliberately the vars
        // the agent's environment already carries rather than new ones: the
        // gate refuses every call when any is missing, so adding a bespoke var
        // here would mean a new way for the proxy to be silently disarmed.
        AGT_HOST: '${AGT_HOST}',
        AGT_API_KEY: '${AGT_API_KEY}',
        AGT_AGENT_ID: input.agentId,
        // The credential. `${...}` is Claude Code's spawn-time substitution
        // from the agent's environment, which the manager populates from
        // `.env.integrations` — the same path every other integration
        // credential travels.
        [COMPUTER_USE_SSH_KEY_ENV]: `\${${COMPUTER_USE_SSH_KEY_ENV}}`,
        // ssh must be resolvable, and it is not a node builtin.
        PATH: process.env['PATH'] ?? '',
        HOME: process.env['HOME'] ?? '',
      },
    },
  };
}
